import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ApiError } from '../api/client';
import { useCreatePosterFromTemplate, usePosterTemplates, useProviders } from '../api/hooks';
import {
  POSTER_FORMATS,
  RENDER_MODES,
  type ImageProvider,
  type PosterFormat,
  type PosterRenderMode,
  type PosterTemplateDefinition,
} from '../api/types';
import ErrorBanner from '../components/ErrorBanner';
import PosterFieldInput from '../components/PosterFieldInput';

/** Every field starts at whatever the template says its default is. */
function initialValues(template: PosterTemplateDefinition): Record<string, string> {
  return Object.fromEntries(template.fields.map((field) => [field.key, field.defaultValue ?? '']));
}

export default function NewPosterPage() {
  const navigate = useNavigate();
  const templates = usePosterTemplates();
  const providers = useProviders();
  const createPoster = useCreatePosterFromTemplate();

  const [pendingAction, setPendingAction] = useState<'save' | 'generate' | null>(null);
  const [selected, setSelected] = useState<PosterTemplateDefinition | null>(null);
  const [values, setValues] = useState<Record<string, string>>({});
  // Default to a model that is actually usable rather than assuming OpenAI: picking an
  // unconfigured provider only fails once the form is submitted.
  const [providerOverride, setProviderOverride] = useState<ImageProvider | null>(null);
  const [model, setModel] = useState<string | null>(null);
  const [renderMode, setRenderMode] = useState<PosterRenderMode>('FullyAiGenerated');
  const [format, setFormat] = useState<PosterFormat>('Portrait45');

  const usableProviders = useMemo(
    () => (providers.data ?? []).filter((p) => p.available),
    [providers.data],
  );
  const provider = providerOverride ?? usableProviders[0]?.provider ?? null;
  const noProviders = providers.data !== undefined && usableProviders.length === 0;
  const modelsForProvider = (providers.data ?? []).find((p) => p.provider === provider)?.models ?? [];
  // Switching provider invalidates the chosen model, so fall back to that provider's default.
  const selectedModel = model && modelsForProvider.includes(model) ? model : (modelsForProvider[0] ?? null);

  // Validation comes back from the API keyed by field, so it can sit against the right input.
  const fieldErrors = useMemo<Record<string, string>>(() => {
    if (!(createPoster.error instanceof ApiError) || !createPoster.error.validation) {
      return {};
    }
    return Object.fromEntries(
      Object.entries(createPoster.error.validation).map(([key, messages]) => [key, messages[0] ?? '']),
    );
  }, [createPoster.error]);

  const choose = (template: PosterTemplateDefinition) => {
    setSelected(template);
    setValues(initialValues(template));
    setFormat(template.defaultFormat);
    createPoster.reset();
  };

  // Saving a draft records the details without calling an image model, so it works even when no
  // provider has a key. Generating needs one.
  const save = async (generate: boolean) => {
    if (!selected) return;

    setPendingAction(generate ? 'generate' : 'save');
    try {
      const created = await createPoster.mutateAsync({
        template: selected.template,
        values,
        provider,
        renderMode,
        format,
        model: selectedModel,
        generate,
      });

      void navigate(`/tournaments/${created.tournamentId}`);
    } finally {
      setPendingAction(null);
    }
  };

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    void save(true);
  };

  if (templates.isLoading) {
    return <p className="muted">Loading templates...</p>;
  }

  if (templates.error) {
    return <ErrorBanner error={templates.error} />;
  }

  return (
    <div className="stack">
      <nav className="breadcrumb">
        <Link to="/">Competitions</Link> <span aria-hidden="true">/</span> New poster
      </nav>

      <div className="page-head">
        <div>
          <h1>New poster</h1>
          <p className="muted">
            Pick a template, fill in its details, and the poster is queued straight away.
          </p>
        </div>
        {selected && (
          <button type="button" className="button button--ghost" onClick={() => setSelected(null)}>
            Change template
          </button>
        )}
      </div>

      {noProviders && (
        <div className="banner banner--error" role="alert">
          <strong>No image model is available.</strong> Neither ChatGPT Image nor Nano Banana has an
          API key in this environment, so posters cannot be generated yet. Add the Key Vault secrets
          and restart the API.
        </div>
      )}

      {!selected && (
        <div className="grid grid--cards">
          {templates.data?.map((template) => (
            <button
              key={template.template}
              type="button"
              className="card card--choice"
              onClick={() => choose(template)}
            >
              <h2>{template.name}</h2>
              <p className="muted">{template.description}</p>
              <p className="muted small">
                {template.fields.filter((f) => f.required).length} required field
                {template.fields.filter((f) => f.required).length === 1 ? '' : 's'} &middot;{' '}
                {template.fields.length} in total
              </p>
            </button>
          ))}
        </div>
      )}

      {selected && (
        <form className="card form" onSubmit={(e) => void submit(e)}>
          <div>
            <h2>{selected.name}</h2>
            <p className="muted">{selected.description}</p>
          </div>

          <div className="grid grid--2">
            {selected.fields
              .filter((field) => field.type !== 'List' && field.type !== 'MultilineText')
              .map((field) => (
                <PosterFieldInput
                  key={field.key}
                  field={field}
                  value={values[field.key] ?? ''}
                  error={fieldErrors[field.key]}
                  onChange={(value) => setValues((v) => ({ ...v, [field.key]: value }))}
                />
              ))}
          </div>

          {selected.fields
            .filter((field) => field.type === 'List' || field.type === 'MultilineText')
            .map((field) => (
              <PosterFieldInput
                key={field.key}
                field={field}
                value={values[field.key] ?? ''}
                error={fieldErrors[field.key]}
                onChange={(value) => setValues((v) => ({ ...v, [field.key]: value }))}
              />
            ))}

          <details className="advanced">
            <summary>How it is generated</summary>
            <div className="grid grid--2">
              <label>
                Image model
                <select
                  value={provider ?? ''}
                  onChange={(e) => {
                    setProviderOverride(e.target.value as ImageProvider);
                    setModel(null);
                  }}
                >
                  {(providers.data ?? []).map((p) => (
                    <option key={p.provider} value={p.provider} disabled={!p.available}>
                      {p.displayName} ({p.modelId}){p.available ? '' : ' - no API key'}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Model
                <select
                  value={selectedModel ?? ''}
                  disabled={modelsForProvider.length === 0}
                  onChange={(e) => setModel(e.target.value)}
                >
                  {modelsForProvider.map((m, index) => (
                    <option key={m} value={m}>
                      {m}
                      {index === 0 ? ' (default)' : ''}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Format
                <select value={format} onChange={(e) => setFormat(e.target.value as PosterFormat)}>
                  {POSTER_FORMATS.map((f) => (
                    <option key={f.value} value={f.value}>
                      {f.label} - {f.hint}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <fieldset className="modes">
              <legend>How should the words get onto the poster?</legend>
              {RENDER_MODES.map((mode) => (
                <label key={mode.value} className="modes__option">
                  <input
                    type="radio"
                    name="renderMode"
                    value={mode.value}
                    checked={renderMode === mode.value}
                    onChange={(e) => setRenderMode(e.target.value as PosterRenderMode)}
                  />
                  <span>
                    <strong>{mode.label}</strong>
                    <small className="muted">{mode.hint}</small>
                  </span>
                </label>
              ))}
            </fieldset>
          </details>

          <ErrorBanner error={createPoster.error} />

          <div className="row row--wrap">
            <button
              type="submit"
              className="button button--primary"
              disabled={createPoster.isPending || !provider}
            >
              {pendingAction === 'generate' ? 'Generating...' : 'Save and generate'}
            </button>
            <button
              type="button"
              className="button"
              disabled={createPoster.isPending}
              onClick={() => void save(false)}
            >
              {pendingAction === 'save' ? 'Saving...' : 'Save'}
            </button>
            <button type="button" className="button button--ghost" onClick={() => setSelected(null)}>
              Back
            </button>
          </div>
          <p className="muted small">
            <strong>Save</strong> keeps the details without making an image - useful for setting next
            week up in advance, and you can generate it later from the competition.{' '}
            <strong>Save and generate</strong> also queues the image, which takes roughly 20 to 60
            seconds.
          </p>
        </form>
      )}
    </div>
  );
}
